import RemotasksExternalLink from 'corp/components/UI/RemotasksExternalLink';
import React, { useMemo } from 'react';
import qs from 'qs';
import { LookupObject } from 'react-scale/corp/components/Lookup/LookupTypes';

import { TaskType } from '@scale/shared-types';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { CustomQuickAction } from 'react-scale/corp/components/SmartLink/SmartLink';
import { getLidarLiteLink } from 'react-scale/lib/helpers/redirectHelpers';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

const generateAuditLinks = (
  isSpoofed: boolean,
  id: string,
  customerEmail: string,
  stagesToAudit: string[] | undefined,
  customerId: string,
  isRemoAdmin: boolean,
  useLidarLite?: boolean,
  cameraNum?: string,
) => {
  const baseQuery: Record<string, any> = {
    taskId: id,
  };

  // if we specify a valid cameraNum, add it to the query params
  if (cameraNum !== undefined) {
    baseQuery.cameraId = cameraNum;
  }

  if (isSpoofed) {
    baseQuery.spoof = customerId;
  }

  const baseURL = isRemoAdmin ? 'remoadmin/tools/internal_audit' : 'audit';

  const phrasing = isSpoofed ? `View as ${customerEmail}` : `View as admin`;
  const opts = isSpoofed
    ? {
        target: '_blank',
        rel: 'noopener noreferrer',
      }
    : {};

  if (useLidarLite) {
    return (
      <a href={getLidarLiteLink(baseQuery)} {...opts}>
        {phrasing}
      </a>
    );
  }

  if (!stagesToAudit?.length) {
    return (
      <a href={`/${baseURL}?` + qs.stringify(baseQuery)} {...opts}>
        {phrasing}
      </a>
    );
  }

  const numStages = stagesToAudit.length;

  return stagesToAudit.map((stageToAudit, idx) => {
    return (
      <a
        href={
          `/${baseURL}?` +
          qs.stringify({
            ...baseQuery,
            stageToAudit,
          })
        }
        {...opts}
        key={idx}
      >
        {`${phrasing} (${idx + 1} / ${numStages})`}
      </a>
    );
  });
};

const TaskShortcuts: React.FC<{
  type: string;
  id: string;
  isLidar: boolean;
  isPending: boolean;
  customerEmail: string;
  isComplete: boolean;
  stagesToAudit?: string[];
  customerId: string;
  task?: LookupObject;
  customQuickAction?: CustomQuickAction;
  user: string;
  cameraNum?: string;
  config?: Record<string, any>;
  // eslint-disable-next-line complexity
}> = ({
  type,
  id,
  isLidar,
  isPending,
  customerEmail,
  stagesToAudit,
  isComplete,
  customerId,
  task,
  customQuickAction,
  cameraNum,
  config,
}) => {
  // hidePII === false only when user is QM (not QA)
  const { isRemoAdmin, hidePII } = useRemoAdminCtx();
  const audit2DLTDinLLEnabled = useUnleashFeatureFlag(
    'audit2DLTDinLidarlite',
    { projectId: config?.projectId },
    false,
  );

  const useLidarLite = useMemo(() => {
    if (!task?.type) return false;

    if (
      task?.type === TaskType.LidarSegmentation ||
      task?.type === TaskType.SensorFusion ||
      task?.type === TaskType.LidarAnnotation
    ) {
      return true;
    }

    if (!audit2DLTDinLLEnabled) return false;

    const isFixlessAuditEnabled = !!config?.enableFixlessAudits?.value;
    if (!isFixlessAuditEnabled) return false;

    if (task?.type !== TaskType.VideoAnnotation && task.type !== TaskType.LidarTopdown)
      return false;

    return true;
  }, [config?.enableFixlessAudits?.value, task?.type, audit2DLTDinLLEnabled]);

  const isVideoCaptioning = task?.type === TaskType.VideoCaptioning;

  const useCustomUrl = isVideoCaptioning || isLidar;

  return (
    <>
      {isPending && isLidar && (
        <>
          <RemotasksExternalLink path="/tasks" query={{ [`${type}Id`]: id, force: 1, readOnly: 1 }}>
            View as tasker in read only mode (without claim)
          </RemotasksExternalLink>
          <RemotasksExternalLink path="/tasks" query={{ [`${type}Id`]: id, forceClaim: 1 }}>
            View as tasker in edit mode (with claim)
          </RemotasksExternalLink>
        </>
      )}

      {isPending && isVideoCaptioning && (
        <>
          <RemotasksExternalLink isOutlier path={`/videocaptioning/task`} query={{ taskId: id }}>
            View as tasker
          </RemotasksExternalLink>
          <RemotasksExternalLink
            isOutlier
            path={`/videocaptioning/task`}
            query={{ taskId: id, forceClaim: 1 }}
            confirm="Confirm you would like to claim this task. By doing so, you are removing the current claim on the task."
          >
            Claim and view as tasker
          </RemotasksExternalLink>
        </>
      )}

      {isPending && !useCustomUrl && (
        <RemotasksExternalLink path="/tasks" query={{ [`${type}Id`]: id, readOnly: 1 }}>
          View on Remotasks
        </RemotasksExternalLink>
      )}

      {isPending && !useCustomUrl && (
        <RemotasksExternalLink isOutlier path="/tasks" query={{ [`${type}Id`]: id, force: 1 }}>
          View on Outlier
        </RemotasksExternalLink>
      )}

      {!useCustomUrl && isPending && (
        <RemotasksExternalLink
          path="/tasks"
          query={{ [`${type}Id`]: id, forceClaim: 1 }}
          confirm="Confirm you would like to claim this task. By doing so, you are removing the current claim on the task."
        >
          Claim and view as tasker
        </RemotasksExternalLink>
      )}

      {!useCustomUrl && isPending && (
        <RemotasksExternalLink path={'/preview/' + id}>
          View preview on Remotasks
        </RemotasksExternalLink>
      )}

      {isComplete &&
        !hidePII &&
        generateAuditLinks(
          false,
          task?._id ?? id,
          customerEmail,
          stagesToAudit,
          customerId,
          isRemoAdmin,
          useLidarLite,
          cameraNum,
        )}

      {customerEmail &&
        isComplete &&
        !isRemoAdmin &&
        generateAuditLinks(
          true,
          task?._id ?? id,
          customerEmail,
          stagesToAudit,
          customerId,
          isRemoAdmin,
          useLidarLite,
        )}

      {customQuickAction && <a onClick={customQuickAction.onClick}>{customQuickAction.title}</a>}
    </>
  );
};

export default TaskShortcuts;
